// Pointer-driven rotation for the landing vinyl record. A press-and-drag on
// the disc or tint rotates the record 1:1 around its visual centre, like
// scratching. A single rAF loop owns the .vinyl-spin transform: it adds the
// slow auto-rotation when idle, switches to user-driven deltas mid-drag, and
// resumes auto-rotation from the new angle on release. Composes with
// src/vinyl-tilt.ts (separate transform layers, no conflict). Disabled under
// prefers-reduced-motion: reduce.

const reduce = matchMedia("(prefers-reduced-motion: reduce)");

const spin = document.querySelector<HTMLElement>(".vinyl-spin");
const disc = spin?.querySelector<HTMLElement>(".vinyl-disc");
const tint = spin?.querySelector<HTMLElement>(".vinyl-tint");

if (spin && !reduce.matches) {
  const REV_MS = 66_000;

  let angle = 0;
  let isDragging = false;
  let activePointerId = -1;
  let centerX = 0;
  let centerY = 0;
  let prevAngle = 0;
  let last = performance.now();

  const recomputeCenter = () => {
    const r = spin.getBoundingClientRect();
    centerX = r.left + r.width / 2;
    centerY = r.top + r.height / 2;
  };

  const angleFromPointer = (clientX: number, clientY: number) =>
    (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;

  const tick = (t: number) => {
    const dt = t - last;
    last = t;
    if (!isDragging) angle += (360 / REV_MS) * dt;
    spin.style.transform = `rotate(${angle.toFixed(3)}deg)`;
    requestAnimationFrame(tick);
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    recomputeCenter();
    prevAngle = angleFromPointer(e.clientX, e.clientY);
    activePointerId = e.pointerId;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== activePointerId) return;
    const cur = angleFromPointer(e.clientX, e.clientY);
    let delta = cur - prevAngle;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    angle += delta;
    prevAngle = cur;
    if (delta !== 0) isDragging = true;
  };

  const onPointerUp = (e: PointerEvent) => {
    if (e.pointerId !== activePointerId) return;
    activePointerId = -1;
    isDragging = false;
  };

  for (const handle of [disc, tint]) {
    if (!handle) continue;
    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
  }

  let resizeRaf = 0;
  window.addEventListener("resize", () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      recomputeCenter();
    });
  });

  recomputeCenter();
  requestAnimationFrame(tick);
}
